import { runtimeConfig, resolvedCloudSyncMode } from '../../config/runtime';
import { getAuthState, getValidAuthSession } from '../auth/auth-store';

export type CloudHealth={ok:boolean;service:string;environment:string;release:string;database:boolean;timestamp:string;message?:string};
export type RemoteSyncResult={status:'synced'|'conflict';revision:number;hash:string;value:string|null;updatedAt:string};

const headers=(accessToken?:string)=>({'Content-Type':'application/json',apikey:runtimeConfig.supabaseAnonKey,Authorization:`Bearer ${accessToken??runtimeConfig.supabaseAnonKey}`});

const apiErrorMessage=(payload:unknown,fallback:string)=>{
  if(typeof payload==='string'&&payload.trim())return payload;
  if(payload&&typeof payload==='object'){
    const record=payload as Record<string,unknown>;
    for(const candidate of [record.message,record.error,record.details,record.hint]){
      if(typeof candidate==='string'&&candidate.trim())return candidate;
      if(candidate&&typeof candidate==='object'){
        const nested=candidate as Record<string,unknown>;
        const message=[nested.message,nested.details,nested.hint]
          .find((value)=>typeof value==='string'&&value.trim());
        if(typeof message==='string')return message;
      }
    }
  }
  return fallback;
};

export const isAuthenticationFailure=(status:number,payload:unknown)=>{
  if(status===401)return true;
  const message=apiErrorMessage(payload,'');
  return /invalid jwt|jwt expired|token.*expired|sesi[oó]n inv[aá]lida|sesi[oó]n vencida/i.test(message);
};

const sessionForRequest=async(publicRequest=false)=>{
  if(publicRequest)return null;
  const session=await getValidAuthSession();
  if(!session)throw new Error('Necesitas una sesión activa para usar la nube.');
  return session;
};

export const cloudConfigured=()=>resolvedCloudSyncMode==='supabase'&&Boolean(runtimeConfig.functionsUrl);

export async function invokeCloud<T>(name:string,body:Record<string,unknown>={},options:{public?:boolean}={}):Promise<T>{
  if(!runtimeConfig.functionsUrl)throw new Error('Las funciones de nube no están configuradas.');
  let session=await sessionForRequest(options.public);
  const request=()=>fetch(`${runtimeConfig.functionsUrl}/${name}`,{method:'POST',headers:headers(session?.accessToken),body:JSON.stringify(body)});
  let response=await request();
  let payload=await response.json().catch(()=>({})) as Record<string,unknown>;
  if(!options.public&&session&&isAuthenticationFailure(response.status,payload)){
    session=await getValidAuthSession({force:true,rejectedAccessToken:session.accessToken});
    if(!session)throw new Error('Tu sesión venció. Inicia sesión nuevamente.');
    response=await request();
    payload=await response.json().catch(()=>({})) as Record<string,unknown>;
  }
  if(!response.ok)throw new Error(apiErrorMessage(payload,`La función ${name} respondió ${response.status}.`));
  return payload as T;
}

export async function invokeSupabaseRpc<T>(name:string,body:Record<string,unknown>={},options:{public?:boolean}={}):Promise<T>{
  if(!runtimeConfig.supabaseUrl||!runtimeConfig.supabaseAnonKey)throw new Error('Supabase no está configurado.');
  let session=await sessionForRequest(options.public);
  const request=()=>fetch(`${runtimeConfig.supabaseUrl}/rest/v1/rpc/${name}`,{
    method:'POST',
    headers:headers(session?.accessToken),
    body:JSON.stringify(body),
  });
  let response=await request();
  let payload=await response.json().catch(()=>null) as T|Record<string,unknown>|null;
  if(!options.public&&session&&isAuthenticationFailure(response.status,payload)){
    session=await getValidAuthSession({force:true,rejectedAccessToken:session.accessToken});
    if(!session)throw new Error('Tu sesión venció. Inicia sesión nuevamente.');
    response=await request();
    payload=await response.json().catch(()=>null) as T|Record<string,unknown>|null;
  }
  if(!response.ok)throw new Error(apiErrorMessage(payload,`La función ${name} respondió ${response.status}.`));
  return payload as T;
}

export async function getCloudHealth():Promise<CloudHealth>{
  if(!runtimeConfig.functionsUrl)return {ok:false,service:'nexo-cloud',environment:runtimeConfig.environment,release:runtimeConfig.releaseVersion,database:false,timestamp:new Date().toISOString(),message:'Supabase no configurado'};
  const response=await fetch(`${runtimeConfig.functionsUrl}/health`,{method:'POST',headers:headers(getAuthState().session?.accessToken),body:'{}'});
  const payload=await response.json().catch(()=>({})) as CloudHealth;
  return response.ok?payload:{...payload,ok:false,message:payload.message??`HTTP ${response.status}`};
}

export const pushCloudSync=(operation:Record<string,unknown>)=>invokeCloud<RemoteSyncResult>('sync-push',{operation});
export const pullCloudSync=(since?:string)=>invokeCloud<{changes:Array<{entityKey:string;value:string|null;hash:string;revision:number;updatedAt:string}>;serverTime:string}>('sync-pull',{since});
export const syncCatalogSnapshot=(catalog:Record<string,unknown>)=>invokeSupabaseRpc<{ok:boolean}>('sync_catalog_snapshot',{snapshot:catalog});
export const getPublicCatalog=(slug:string)=>invokeSupabaseRpc<Record<string,unknown>|null>('get_public_catalog',{requested_slug:slug},{public:true});
export const askProductionAi=(input:Record<string,unknown>)=>invokeCloud<{answer:string;proposal?:Record<string,unknown>;requestId:string}>('nexo-ai',input);
export const createCheckout=(planId:string)=>invokeCloud<{url:string;provider:string}>('billing-checkout',{planId,returnUrl:`${runtimeConfig.siteUrl}/app/plan`});
export const sendTapassEvent=(event:Record<string,unknown>)=>invokeCloud<{accepted:boolean;externalId?:string}>('tapass-bridge',{event});
export const requestLocalLicense=(organizationId:string,installationId:string)=>invokeCloud<{license:Record<string,unknown>}>('issue-license',{organizationId,installationId});
